import { useQueryClient } from '@tanstack/react-query';
import {
  type AddDraftNoteRequest,
  type UpdateDraftNoteRequest,
  type PlaceDraftNoteRequest,
  type DraftNoteResponse,
  type DeleteDraftNoteResponse,
  type ProcessDraftNote
} from '@bliks-io/contracts';
import { apiClient } from '../../client';
import { useDraftMutation } from '../helpers/draftMutation';
import {
  optimisticDraftUpdate,
  applyDraftCache,
  restoreDraftCache
} from '../helpers/draftCacheHelpers';
import { basePath, mintTempDraftNoteId } from './shared';

const notesPath = (processId: string) => `${basePath(processId)}/notes`;

export function useAddDraftNote(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async (body: AddDraftNoteRequest): Promise<DraftNoteResponse> => {
      const { data } = await apiClient.post<DraftNoteResponse>(notesPath(processId), body);
      return data;
    },
    onMutate: async (body) => {
      const tempId = mintTempDraftNoteId();
      const optimistic = await optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        draft: { ...old.draft, updatedAt: new Date() },
        notes: [
          ...old.notes,
          {
            _id: tempId,
            draftId: old.draft._id,
            processId,
            text: body.text,
            category: body.category,
            geometry: body.geometry,
            targets: body.targets,
            createdBy: '',
            updatedAt: new Date()
          } satisfies ProcessDraftNote
        ]
      }));
      return { ...optimistic, tempId };
    },
    onError: (_err, _vars, ctx) => restoreDraftCache(queryClient, processId, ctx),
    onSuccess: (res, _vars, ctx) => {
      const tempId = ctx?.tempId;
      if (!tempId) return;
      applyDraftCache(queryClient, processId, (old) => ({
        ...old,
        notes: old.notes.map((n) => (n._id === tempId ? res.note : n))
      }));
    }
  });
}

export function useUpdateDraftNote(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      noteId,
      patch
    }: {
      noteId: string;
      patch: UpdateDraftNoteRequest;
    }): Promise<DraftNoteResponse> => {
      const { data } = await apiClient.patch<DraftNoteResponse>(
        `${notesPath(processId)}/${noteId}`,
        patch
      );
      return data;
    },
    onMutate: async ({ noteId, patch }) =>
      optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        notes: old.notes.map((n) => (n._id === noteId ? { ...n, ...patch } : n))
      })),
    onError: (_err, _vars, ctx) => restoreDraftCache(queryClient, processId, ctx),
    onSuccess: (res) => {
      applyDraftCache(queryClient, processId, (old) => ({
        ...old,
        notes: old.notes.map((n) => (n._id === res.note._id ? res.note : n))
      }));
    }
  });
}

export function usePlaceDraftNote(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      noteId,
      patch
    }: {
      noteId: string;
      patch: PlaceDraftNoteRequest;
    }): Promise<DraftNoteResponse> => {
      const { data } = await apiClient.patch<DraftNoteResponse>(
        `${notesPath(processId)}/${noteId}/placement`,
        patch
      );
      return data;
    },
    onMutate: async ({ noteId, patch }) =>
      optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        notes: old.notes.map((n) =>
          n._id === noteId
            ? {
                ...n,
                ...(patch.geometry ? { geometry: patch.geometry } : {}),
                ...(patch.targets ? { targets: patch.targets } : {})
              }
            : n
        )
      })),
    onError: (_err, _vars, ctx) => restoreDraftCache(queryClient, processId, ctx),
    onSuccess: (res) => {
      applyDraftCache(queryClient, processId, (old) => ({
        ...old,
        notes: old.notes.map((n) => (n._id === res.note._id ? res.note : n))
      }));
    }
  });
}

export function useDeleteDraftNote(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async (noteId: string): Promise<DeleteDraftNoteResponse> => {
      const { data } = await apiClient.delete<DeleteDraftNoteResponse>(
        `${notesPath(processId)}/${noteId}`
      );
      return data;
    },
    onMutate: async (noteId) =>
      optimisticDraftUpdate(queryClient, processId, (old) => ({
        ...old,
        draft: { ...old.draft, updatedAt: new Date() },
        notes: old.notes.filter((n) => n._id !== noteId)
      })),
    onError: (_err, _vars, ctx) => restoreDraftCache(queryClient, processId, ctx)
  });
}
